import { Check, Copy } from "lucide-react";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { forwardRef, useCallback } from "react";
import { Input } from "@/components/podkit/forms/Input";
import { Button } from "@/components/flexkit/Button";
import type { PropsWithClassName } from "@/components/podkit/lib/cn";
import { cn } from "@/components/podkit/lib/cn";
import type { WithTrackingId } from "@/types/tracking";
type InputProps = React.ComponentPropsWithoutRef<"input"> & {
    copyable?: boolean;
    value: string;
    title?: string;
    wrapperClassName?: string;
} & WithTrackingId &
    PropsWithClassName;
const CopyableInput = forwardRef<HTMLInputElement, InputProps>(
    ({ value, title, "data-tracking-id": dataTrackingId, wrapperClassName, ...props }, ref) => {
        const [copied, setCopied] = useTemporaryState(false, 2000);

        const handleCopyToClipboard = useCallback(() => {
            void navigator.clipboard.writeText(value).then(() => {
                setCopied(true);
            });
        }, [setCopied, value]);

        return (
            <div className={cn("relative", wrapperClassName)}>
                <Input className="pr-12 ring-inset" readOnly value={value} {...props} ref={ref} />
                <div className={cn("absolute inset-y-0 right-2 flex items-center", { hidden: !value })}>
                    <Button
                        variant={"ghost"}
                        type="button"
                        className="h-6 rounded-lg border-none p-1 text-content-tertiary hover:text-content-secondary hover:opacity-100"
                        onClick={handleCopyToClipboard}
                        aria-label={title ?? "Copy to clipboard"}
                        title={title}
                        data-tracking-id={dataTrackingId}
                    >
                        {copied ? (
                            <Check className="text-content-success" aria-hidden size={16} />
                        ) : (
                            <Copy aria-hidden size={16} />
                        )}
                    </Button>
                </div>
            </div>
        );
    },
);
CopyableInput.displayName = "CopyableInput";

export { CopyableInput };
